import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { DataLabel, ScoreRing } from "../components/common";
import GenerateDialog from "../components/GenerateDialog";
import { FailedTodayBanner, SchedulerJobs } from "../components/SchedulerPanel";
import {
  Flask, MagnifyingGlass, ShareNetwork, ClockCountdown, Sparkle, CheckCircle, XCircle,
  CircleNotch, Warning, ArrowRight, Lightning, Play, TrendUp, TrendDown, ChartLineUp,
  FileText, ArrowsClockwise, ArrowUpRight,
} from "@phosphor-icons/react";

const AGENTS = [
  { key: "scientific_watch", label: "Veille scientifique", icon: Flask, desc: "PubMed · pertinence" },
  { key: "gap_analysis", label: "Analyse des lacunes", icon: MagnifyingGlass, desc: "Sujets manquants" },
  { key: "internal_linking", label: "Maillage interne", icon: ShareNetwork, desc: "Recalcul des liens" },
  { key: "obsolescence", label: "Obsolescence", icon: ClockCountdown, desc: "Contenus à actualiser" },
];

const PRIO = {
  critical: { label: "Critique", cls: "bg-red-50 text-red-700 border-red-200", dot: "#dc2626" },
  high: { label: "Élevé", cls: "bg-orange-50 text-warn border-orange-200", dot: "#D95D39" },
  medium: { label: "Moyen", cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "#b45309" },
  low: { label: "Faible", cls: "bg-secondary text-muted-foreground border-border", dot: "#94a3b8" },
};

function PriorityTag({ level, testid }) {
  const p = PRIO[level] || PRIO.low;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wide border ${p.cls}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.dot }} />{p.label}
    </span>
  );
}

const healthColor = (v) => v >= 80 ? "#0B3B2D" : v >= 60 ? "#b45309" : "#dc2626";
const healthLabel = (v) => v >= 80 ? "Excellente" : v >= 60 ? "Correcte" : "À renforcer";
const BREAKDOWN_META = {
  seo: { label: "SEO", route: "/contenus" },
  geo: { label: "GEO (moteurs IA)", route: "/contenus" },
  freshness: { label: "Fraîcheur scientifique", route: "/validation" },
  validation_flow: { label: "Flux de validation", route: "/validation" },
  coverage: { label: "Couverture thématique", route: "/calendrier" },
};

function QuickStat({ label, value, accent, testid, onClick }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="text-left bg-white border border-border rounded-md p-4 hover:-translate-y-[1px] hover:shadow-md transition-transform">
      <DataLabel>{label}</DataLabel>
      <div className="font-serif text-3xl mt-1.5" style={{ color: accent }}>{value}</div>
    </button>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [running, setRunning] = useState(false);
  const nav = useNavigate();
  const load = () => api.get("/dashboard").then((r) => setD(r.data));
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const runAgent = async (key, label) => {
    try { await api.post(`/agents/${key}/run`); toast.success(`${label} lancé`); setTimeout(load, 800); }
    catch { toast.error("Lancement impossible"); }
  };

  const runFullCycle = async () => {
    setRunning(true);
    try { await api.post("/agents/daily_cycle/run"); toast.success("Cycle complet lancé — veille → génération → SEO/GEO → validation"); setTimeout(load, 1000); }
    catch { toast.error("Impossible de lancer le cycle"); }
    finally { setTimeout(() => setRunning(false), 1500); }
  };

  const proposeRevision = async (id) => {
    try { await api.post(`/content/${id}/propose-revision`); toast.success("Proposition de mise à jour en préparation par l'IA"); setTimeout(load, 1000); }
    catch { toast.error("Impossible de générer la proposition"); }
  };

  if (!d) return <div className="font-mono text-xs text-muted-foreground">Chargement du centre de commande…</div>;

  const runIcon = (s) => s === "success" ? <CheckCircle weight="fill" className="text-emerald-600" size={16} />
    : s === "error" ? <XCircle weight="fill" className="text-red-500" size={16} />
    : <CircleNotch className="text-ai ai-pulse" size={16} />;

  const nextCycle = (d.next_runs || []).find((r) => r.id === "daily_cycle");
  const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

  return (
    <div data-testid="command-center">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground mb-1">Command Center</div>
          <h1 className="font-serif text-4xl text-brand tracking-tight">Santé éditoriale du site</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="run-full-cycle-btn" onClick={runFullCycle} disabled={running}
            className="flex items-center gap-2 bg-brand text-white hover:bg-brand-hover disabled:opacity-60 px-4 py-2 rounded-md text-sm font-medium transition-colors">
            {running ? <CircleNotch size={16} className="ai-pulse" /> : <Play size={16} weight="fill" />} Exécuter le cycle complet
          </button>
          <GenerateDialog onStarted={load} trigger={
            <button data-testid="new-content-btn" className="flex items-center gap-2 border border-brand text-brand hover:bg-brand-soft px-4 py-2 rounded-md text-sm font-medium transition-colors">
              <Sparkle size={16} weight="fill" /> Nouveau contenu
            </button>} />
        </div>
      </div>

      <FailedTodayBanner />

      {/* HERO: health score + breakdown + recommended actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div data-testid="health-score-card" className="bg-white border border-border rounded-md p-6 flex flex-col items-center justify-center text-center">
          <ScoreRing value={d.health_score} size={148} accent={healthColor(d.health_score)} />
          <div className="mt-2">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Score de santé éditoriale</div>
            <div className="font-serif text-xl mt-0.5" style={{ color: healthColor(d.health_score) }}>{healthLabel(d.health_score)}</div>
          </div>
        </div>

        <div className="bg-white border border-border rounded-md p-5">
          <DataLabel>Décomposition — cliquez pour le détail</DataLabel>
          <div className="mt-3 space-y-2.5">
            {Object.entries(d.health_breakdown || {}).map(([k, v]) => {
              const m = BREAKDOWN_META[k] || { label: k, route: "/" };
              return (
                <button key={k} data-testid={`breakdown-${k}`} onClick={() => nav(m.route)}
                  className="w-full group">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-foreground group-hover:text-brand transition-colors">{m.label}</span>
                    <span className="font-mono" style={{ color: healthColor(v) }}>{v}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-sand overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${v}%`, background: healthColor(v) }} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-border rounded-md p-5">
          <div className="flex items-center gap-2 mb-3"><Lightning size={16} weight="fill" className="text-warn" /><DataLabel>Actions prioritaires (IA)</DataLabel></div>
          <div className="space-y-2">
            {(d.recommended_actions || []).length === 0 && <div className="text-sm text-muted-foreground py-4 text-center">Tout est à jour. 🎉</div>}
            {(d.recommended_actions || []).map((a, i) => (
              <button key={i} data-testid={`recommended-action-${i}`} onClick={() => nav(a.route)}
                className="w-full text-left border border-border rounded-md p-3 hover:border-brand/40 hover:bg-bone transition-colors flex items-start gap-2">
                <PriorityTag level={a.priority} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium leading-snug">{a.label}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{a.detail}</div>
                </div>
                <ArrowRight size={14} className="text-muted-foreground mt-0.5 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Quick stats strip */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-8">
        <QuickStat testid="qs-pending" label="À valider" value={d.pending_validation} accent="#b45309" onClick={() => nav("/validation")} />
        <QuickStat testid="qs-revisions" label="MàJ proposées" value={d.revision_proposals || 0} accent="#D95D39" onClick={() => nav("/validation")} />
        <QuickStat testid="qs-drafts" label="Brouillons" value={d.drafts} accent="#64748b" onClick={() => nav("/brouillons")} />
        <QuickStat testid="qs-opportunities" label="Opportunités" value={d.new_topics} accent="#0B3B2D" onClick={() => nav("/calendrier")} />
        <QuickStat testid="qs-published" label="Publiés" value={d.published} accent="#166534" onClick={() => nav("/contenus")} />
        <QuickStat testid="qs-studies" label="Études suivies" value={d.studies_tracked} accent="#2563EB" onClick={() => nav("/veille")} />
      </div>

      {/* Alerts + To-update */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Scientific alerts */}
        <div>
          <div className="flex items-center gap-2 mb-3"><Flask size={16} className="text-ai" /><DataLabel>Alertes scientifiques prioritaires</DataLabel></div>
          <div className="space-y-3" data-testid="alerts-list">
            {(d.alerts || []).length === 0 && <div className="bg-white border border-border rounded-md p-6 text-sm text-muted-foreground text-center">Aucune alerte à forte pertinence pour le moment.</div>}
            {(d.alerts || []).map((a, i) => (
              <div key={i} data-testid={`alert-${i}`} className="bg-white border border-border rounded-md p-4">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <PriorityTag level={a.priority} />
                  <span className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground">{a.reason}</span>
                  <span className="ml-auto text-[11px] font-mono text-ai">Pertinence {a.relevance_score}</span>
                </div>
                <div className="text-sm font-medium leading-snug">{a.title}</div>
                {a.journal && <div className="text-[11px] text-muted-foreground mt-0.5">{a.journal}</div>}
                <div className="text-xs text-foreground mt-2"><span className="text-muted-foreground">Recommandation : </span>{a.recommended_change}</div>
                {(a.affected_pages || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border">
                    <div className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground mb-1">Pages potentiellement concernées</div>
                    <div className="flex flex-wrap gap-1.5">
                      {a.affected_pages.map((p) => (
                        <button key={p.id} data-testid={`alert-${i}-page-${p.id}`} onClick={() => nav(`/validation?content=${p.id}`)}
                          className="text-[11px] px-2 py-0.5 rounded bg-brand-soft text-brand hover:bg-brand hover:text-white transition-colors line-clamp-1 max-w-[220px]">
                          {p.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {a.url && <a data-testid={`alert-${i}-source`} href={a.url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] font-mono text-ai hover:underline mt-2">
                  Publication originale <ArrowUpRight size={12} /></a>}
              </div>
            ))}
          </div>
        </div>

        {/* Contents to update */}
        <div>
          <div className="flex items-center gap-2 mb-3"><ClockCountdown size={16} className="text-warn" /><DataLabel>Contenus à actualiser</DataLabel></div>
          <div className="space-y-3" data-testid="to-update-list">
            {(d.to_update || []).length === 0 && <div className="bg-white border border-border rounded-md p-6 text-sm text-muted-foreground text-center">Aucun contenu obsolète. Le site est scientifiquement à jour.</div>}
            {(d.to_update || []).map((c) => (
              <div key={c.id} data-testid={`to-update-${c.id}`} className="bg-white border border-border rounded-md p-4">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <PriorityTag level={c.priority} />
                  <span className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground">{c.content_type}</span>
                  <span className="ml-auto text-[10px] font-mono text-muted-foreground">MàJ {fmtDate(c.last_updated)}</span>
                </div>
                <button onClick={() => nav(`/validation?content=${c.revision_id || c.id}`)} className="text-left w-full">
                  <div className="text-sm font-medium leading-snug hover:text-brand transition-colors">{c.title_primary}</div>
                </button>
                <div className="text-xs text-foreground mt-1.5"><span className="text-muted-foreground">Raison : </span>{c.reason}</div>
                {(c.triggering_studies || []).length > 0 && (
                  <div className="mt-2 space-y-0.5">
                    {c.triggering_studies.slice(0, 2).map((s, j) => (
                      <a key={j} href={s.url} target="_blank" rel="noreferrer" className="block text-[11px] text-ai hover:underline truncate">↳ {s.title}</a>
                    ))}
                  </div>
                )}
                <div className="mt-3 flex items-center gap-2">
                  {c.has_revision ? (
                    <button data-testid={`review-revision-${c.id}`} onClick={() => nav(`/validation?content=${c.revision_id}`)}
                      className="flex items-center gap-1.5 text-xs bg-brand text-white hover:bg-brand-hover px-3 py-1.5 rounded-md transition-colors">
                      <CheckCircle size={14} /> Relire la proposition
                    </button>
                  ) : (
                    <button data-testid={`propose-revision-${c.id}`} onClick={() => proposeRevision(c.id)}
                      className="flex items-center gap-1.5 text-xs border border-brand text-brand hover:bg-brand-soft px-3 py-1.5 rounded-md transition-colors">
                      <ArrowsClockwise size={14} /> Générer une proposition de mise à jour
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Weak-evidence articles to reinforce */}
      {(d.weak_evidence || []).length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3"><Warning size={16} className="text-warn" /><DataLabel>Articles à renforcer (preuve faible)</DataLabel></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="weak-evidence-list">
            {d.weak_evidence.map((c) => (
              <div key={c.id} data-testid={`weak-evidence-${c.id}`} className="bg-white border border-border rounded-md p-4">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <PriorityTag level={c.priority} />
                  <span className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground">{c.evidence_label || "Sans preuve"}</span>
                  <span className="ml-auto text-[11px] font-mono text-muted-foreground">{c.references_count} source{c.references_count > 1 ? "s" : ""}</span>
                </div>
                <button onClick={() => nav("/contenus")} className="text-left w-full">
                  <div className="text-sm font-medium leading-snug hover:text-brand transition-colors">{c.title_primary}</div>
                </button>
                <div className="text-xs text-foreground mt-1.5"><span className="text-muted-foreground">Raison : </span>{c.reason}</div>
                <div className="mt-3">
                  <button data-testid={`reinforce-${c.id}`} onClick={() => proposeRevision(c.id)}
                    className="flex items-center gap-1.5 text-xs border border-brand text-brand hover:bg-brand-soft px-3 py-1.5 rounded-md transition-colors">
                    <ArrowsClockwise size={14} /> Renforcer avec de nouvelles sources
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Performance */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3"><ChartLineUp size={16} className="text-emerald-600" /><DataLabel>Performances SEO / GEO</DataLabel></div>
        {!d.performance?.connected ? (
          <div data-testid="perf-connect-cta" className="bg-white border border-dashed border-border rounded-md p-6 text-center">
            <p className="text-sm text-muted-foreground mb-3">Connectez Google Search Console, Bing Webmaster Tools et Google Analytics pour suivre positions, impressions, clics, CTR et détecter les « victoires rapides ».</p>
            <button data-testid="perf-connect-btn" onClick={() => nav("/performance")} className="inline-flex items-center gap-2 border border-brand text-brand hover:bg-brand-soft px-4 py-2 rounded-md text-sm font-medium transition-colors">
              Connecter les intégrations <ArrowRight size={14} />
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white border border-border rounded-md p-4">
              <div className="flex items-center gap-1.5 mb-2 text-emerald-700"><Lightning size={14} weight="fill" /><span className="text-xs font-medium">Victoires rapides</span></div>
              {(d.performance.quick_wins || []).length === 0 ? <div className="text-xs text-muted-foreground">Aucune détectée.</div> :
                d.performance.quick_wins.map((q, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                    <div className="min-w-0"><div className="text-xs truncate">{q.key}</div><div className="text-[10px] text-muted-foreground">Pos. {q.position} → {q.target}</div></div>
                    <button onClick={() => nav("/performance")} className="text-[10px] font-mono text-brand hover:underline shrink-0">Optimiser</button>
                  </div>
                ))}
            </div>
            <div className="bg-white border border-border rounded-md p-4">
              <div className="flex items-center gap-1.5 mb-2 text-brand"><TrendUp size={14} /><span className="text-xs font-medium">Pages top</span></div>
              {(d.performance.top_pages || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-border last:border-0 text-xs">
                  <span className="truncate">{p.key}</span><span className="font-mono text-muted-foreground shrink-0">{p.clicks} clics · {p.ctr}%</span>
                </div>
              ))}
            </div>
            <div className="bg-white border border-border rounded-md p-4">
              <div className="flex items-center gap-1.5 mb-2 text-warn"><TrendDown size={14} /><span className="text-xs font-medium">En déclin</span></div>
              {(d.performance.declining_pages || []).length === 0 ? <div className="text-xs text-muted-foreground">Rien à signaler.</div> :
                d.performance.declining_pages.map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-border last:border-0 text-xs">
                    <span className="truncate">{p.key}</span><span className="font-mono text-warn shrink-0">{p.from} → {p.to}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Engine control: agents + activity + next runs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-3">
            <DataLabel>Pilotage du moteur IA</DataLabel>
            <span className={`inline-flex items-center gap-1.5 text-[11px] font-mono ${d.scheduler_running ? "text-emerald-600" : "text-muted-foreground"}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${d.scheduler_running ? "bg-emerald-500 ai-pulse" : "bg-slate-300"}`} />
              {d.scheduler_running ? "Planificateur actif" : "Arrêté"}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {AGENTS.map((a) => (
              <button key={a.key} data-testid={`run-agent-${a.key}`} onClick={() => runAgent(a.key, a.label)}
                className="text-left bg-white border border-border rounded-md p-4 hover:-translate-y-[1px] hover:shadow-md transition-transform">
                <a.icon size={22} className="text-brand mb-2" />
                <div className="text-sm font-medium">{a.label}</div>
                <div className="text-[11px] text-muted-foreground">{a.desc}</div>
              </button>
            ))}
          </div>
          {nextCycle?.next_run && (
            <div data-testid="next-cycle" className="mt-3 text-[11px] font-mono text-muted-foreground flex items-center gap-1.5">
              <ClockCountdown size={13} /> Prochain cycle complet : {fmtDate(nextCycle.next_run)}
            </div>
          )}
        </div>

        <div>
          <DataLabel>Activité récente du moteur</DataLabel>
          <div className="mt-3 bg-white border border-border rounded-md divide-y divide-border" data-testid="activity-feed">
            {(d.recent_runs || []).length === 0 && <div className="p-4 text-sm text-muted-foreground">Aucune activité pour le moment.</div>}
            {(d.recent_runs || []).map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-4 py-3">
                {runIcon(r.status)}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{r.agent}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{r.summary || r.error || "En cours…"}</div>
                </div>
                {r.items_created > 0 && <span className="text-xs font-mono text-brand">+{r.items_created}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
      <SchedulerJobs />
    </div>
  );
}
